import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Nav() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { id: "catalogue", label: t.nav.catalogue },
    { id: "producteurs", label: t.nav.producteurs },
    { id: "contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const LangSwitch = ({ inline = false }) => (
    <div
      className={`inline-flex items-center border border-white/15 ${
        inline ? "" : ""
      }`}
      role="group"
      aria-label={t.language.label}
      data-testid="lang-switch"
    >
      {["fr", "en"].map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            data-testid={`lang-${code}`}
            className={`px-3 py-1.5 text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 ${
              active
                ? "bg-[#C5A059] text-[#050505]"
                : "text-[#A3A3A3] hover:text-[#F5F5F0]"
            }`}
          >
            {t.language[code]}
          </button>
        );
      })}
    </div>
  );

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.png"
            alt="Trois Couronnes Productions"
            className="h-12 w-12 object-contain transition-transform duration-500 group-hover:scale-105"
          />
          <span className="hidden md:block font-display text-[15px] tracking-wide text-[#F5F5F0]">
            Trois Couronnes
            <span className="block text-[10px] tracking-[0.32em] text-[#9c9b94] uppercase mt-0.5">
              Productions
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleClick(l.id)}
              className="tcp-link text-[13px] tracking-[0.18em] uppercase text-[#F5F5F0] hover:text-[#C5A059]"
            >
              {l.label}
            </button>
          ))}
          <LangSwitch />
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#F5F5F0] p-2"
          aria-label="Menu"
        >
          <div className="space-y-1.5">
            <span className={`block h-px w-6 bg-current transition-all ${open ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`block h-px w-6 bg-current transition-all ${open ? "opacity-0" : ""}`} />
            <span className={`block h-px w-6 bg-current transition-all ${open ? "-translate-y-2 -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => handleClick(l.id)}
                  className="text-left font-display text-2xl text-[#F5F5F0] hover:text-[#C5A059] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-4">
                <LangSwitch inline />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
